<template>
  <vSelect
    v-model="selectedCountry"
    class="CountrySelection"
    :options="countryOptions"
    :clearable="false"
    :reduce="option => option.value"
  />
</template>

<script>
import vSelect from '@/components/form/vSelect'
import { mapGetters } from 'vuex'

export default {
  name: 'CountrySelection',
  components: { vSelect },
  data() {
    return {
      // default comes from env first (will be adjusted if env is not allowed)
      selectedCountry: (process.env.VUE_APP_DEPLOYMENT || 'nl'),
      initialized: false,
      allCountryOptions: [
        { value: 'nl', label: 'NL' },
        { value: 'be', label: 'BE' },
        { value: 'uk', label: 'UK' },
        { value: 'de', label: 'DE' },
      ],
    }
  },
  computed: {
    ...mapGetters('access', [
      'canAccessCountry',
      'municipalityCodeList'
    ]),
    ...mapGetters('deployment', [
      'getDeploymentCode',
    ]),

    superuser() {
      return this.$auth.user?.['https://evmaps.nl/superuser']
    },

    // filter allCountryOptions by what canAccessCountry returns!
    allowedCountrySet() {
      // Superusers can switch to any deployment, so don't restrict their options
      // (matches the switcher visibility rule in ViewerNavBar).
      if (this.superuser) return new Set()
      const allowed = this.canAccessCountry(this.municipalityCodeList) || []
      return new Set(allowed.map(a => String(a).toLowerCase()))
    },

    countryOptions() {
      const allowedLower = this.allowedCountrySet
      // if no restriction, show all options
      if (!allowedLower.size) return this.allCountryOptions
      return this.allCountryOptions.filter(opt => allowedLower.has(opt.value))
    }
  },

  created() {
    // try to respect env first; if env is not allowed and there are allowed options, pick the first allowed
    const envDeployment = this.normalizeCountry(process.env.VUE_APP_DEPLOYMENT || 'nl')
    const storeDeployment = this.normalizeCountry(this.getDeploymentCode)
    const allowedLower = this.allowedCountrySet

    if (!allowedLower.size) {
      // no restriction -> prefer store, fallback to env
      this.selectedCountry = storeDeployment || envDeployment
    } else if (storeDeployment && allowedLower.has(storeDeployment)) {
      this.selectedCountry = storeDeployment
    } else if (allowedLower.has(envDeployment)) {
      this.selectedCountry = envDeployment
    } else {
      // env not allowed -> pick first allowed
      this.selectedCountry = Array.from(allowedLower)[0]
    }
  },

  mounted() {
    const storeDeployment = this.normalizeCountry(this.getDeploymentCode)
    const allowedLower = this.allowedCountrySet

    if (
      storeDeployment &&
      storeDeployment !== this.selectedCountry &&
      (!allowedLower.size || allowedLower.has(storeDeployment))
    ) {
      this.selectedCountry = storeDeployment
    }

    this.$nextTick(() => { this.initialized = true })
  },

  watch: {
    selectedCountry(newCountry) {
      // ignore the initial programmatic set
      if (!this.initialized) return
      if (!newCountry) return
      const normalizedSelection = this.normalizeCountry(newCountry)
      const currentDeployment = this.normalizeCountry(this.getDeploymentCode)
      if (currentDeployment && currentDeployment === normalizedSelection) return
      const audience = process.env.VUE_APP_AUTH_AUDIENCE || ''
      const isDev = audience.includes('dev') || window.location.hostname.startsWith('dev.')
      const url = normalizedSelection === 'be' && isDev
        ? 'https://evmaps-be-dev.netlify.app'
        : `https://${isDev ? 'dev.' : ''}evmaps.${normalizedSelection}`
      window.location.replace(url)
    }
  },

  methods: {
    normalizeCountry(value) {
      return value ? String(value).toLowerCase() : ''
    },
  },
}
</script>

<style>
.CountrySelection {
  min-width: 120px;
}
</style>
